import api from './api';
import { AxiosResponse } from 'axios';

import {
  PhysicalUnit,
  DropDownLengthPhysicalUnit,
  OptionalPhysicalUnit,
  LengthPhysicalUnit,
  TemperaturePhysicalUnit,
  PressurePhysicalUnit,
  VelocityPhysicalUnit,
  VolumetricFlowRatePhysicalUnit,
  ThermalPowerPhysicalUnit,
  HeatTransferCoefficientPhysicalUnit,
  RatioPhysicalUnit,
  DensityPhysicalUnit,
  DynamicViscosityPhysicalUnit,
  SpecificHeatCapacityPhysicalUnit,
  ThermalConductivityPhysicalUnit,
  SpecificThermalResistancePhysicalUnit,
  OptionalLengthPhysicalUnit,
  OptionalTemperaturePhysicalUnit,
  OptionalPressurePhysicalUnit,
  OptionalVelocityPhysicalUnit,
  OptionalVolumetricFlowRatePhysicalUnit,
  OptionalThermalPowerPhysicalUnit,
  OptionalHeatTransferCoefficientPhysicalUnit,
  OptionalRatioPhysicalUnit,
  OptionalDensityPhysicalUnit,
  OptionalDynamicViscosityPhysicalUnit,
  OptionalSpecificHeatCapacityPhysicalUnit,
  OptionalThermalConductivityPhysicalUnit,
  OptionalSpecificThermalResistancePhysicalUnit,
  TemperatureDifferencePhysicalUnit,
  OptionalTemperatureDifferencePhysicalUnit,
  MassFlowRatePhysicalUnit,
} from '../components/common/types/physicalUnits';

// Re-export physical unit types for use in other components
export type {
  PhysicalUnit,
  DropDownLengthPhysicalUnit,
  OptionalPhysicalUnit,
  LengthPhysicalUnit,
  TemperaturePhysicalUnit,
  PressurePhysicalUnit,
  VelocityPhysicalUnit,
  VolumetricFlowRatePhysicalUnit,
  ThermalPowerPhysicalUnit,
  HeatTransferCoefficientPhysicalUnit,
  RatioPhysicalUnit,
  DensityPhysicalUnit,
  DynamicViscosityPhysicalUnit,
  SpecificHeatCapacityPhysicalUnit,
  ThermalConductivityPhysicalUnit,
  SpecificThermalResistancePhysicalUnit,
  OptionalLengthPhysicalUnit,
  OptionalTemperaturePhysicalUnit,
  OptionalPressurePhysicalUnit,
  OptionalVelocityPhysicalUnit,
  OptionalVolumetricFlowRatePhysicalUnit,
  OptionalThermalPowerPhysicalUnit,
  OptionalHeatTransferCoefficientPhysicalUnit,
  OptionalRatioPhysicalUnit,
  OptionalDensityPhysicalUnit,
  OptionalDynamicViscosityPhysicalUnit,
  OptionalSpecificHeatCapacityPhysicalUnit,
  OptionalThermalConductivityPhysicalUnit,
  OptionalSpecificThermalResistancePhysicalUnit
};

// Type definitions
export interface TubeUI {
  id: string;
  name: string;
  tube_number: string;
  material: string;
  tube_family: string;
  outer_diameter: LengthPhysicalUnit;
  wall_thickness: DropDownLengthPhysicalUnit;
  refrigerants: string[];
  diameter_category: string;
}

export interface TubeDropdownValuesAPI {
  tube_family?: string[] | null;
  material?: string[] | null;
  diameter_category?: string[] | null;
  wall_thickness?: DropDownLengthPhysicalUnit[] | null;
  refrigerant?: string[] | null;
  brand_name?: string[] | null;
  suitable_for_fouling?: boolean[] | null;
}

export interface SelectionLabelsBoiling {
  calculation_mode: string[];
  unit_system: string[];
  message_level: string[];
  refrigerant: string[];
  ratio_unit: string[];
  medium_type: string[];
  thermal_power_unit: string[];
  pressure_unit: string[];
  velocity_unit: string[];
  mass_flow_rate_unit: string[];
  volumetric_flow_unit: string[];
  thermal_resistance_unit: string[];
  temperature_unit: string[];
  temperature_difference_unit: string[];
  heattransfer_coefficient_unit: string[];
  length_unit: string[];
  oil_viscosity: string[];
  heatflux_unit: string[];
  specific_heat_capacity_unit: string[];
  dynamic_viscosity_unit: string[];
  density_unit: string[];
  thermal_conductivity_unit: string[];
  tube_arrangement: string[];
}

/**
 * Request body interface for Special Flooded Evaporator API
 * Compatible with HTRI file import functionality
 */
export interface OptionalRequestBodyFloodedEvaporatorAPI {
  evaporator_configuration?: OptionalEvaporatorConfigurationAPI | null;
  fluids?: OptionalFluidsAPI | null;
  system_of_units: string;
  interchangable_input_conditions?: InterChangableOperatingConditionsAPI | null;
  calculation_mode?: string | null;
  correction_factor_outside?: number | null;
  correction_factor_inside?: number | null;
}

export interface OptionalEvaporatorConfigurationAPI {
  tube_count?: number | null;
  number_of_passes?: number | null;
  number_of_tube_rows?: number | null;
  override_tube_rows?: boolean | null;
  tube_arrangement?: string | null;
  plain_segments?: OptionalPlainSegmentsAPI | null;
  tube_name?: string | null;
}

export interface OptionalPlainSegmentsAPI {
  inner_segments?: number | null;
  length_per_end_segment?: OptionalLengthPhysicalUnit | null;
  length_per_inner_segment?: OptionalLengthPhysicalUnit | null;
}

export interface OptionalFluidsAPI {
  refrigerant?: string | null;
  medium?: OptionalMediumAPI | null;
  oil?: OptionalOilAPI | null;
  fouling_factor_inside?: OptionalSpecificThermalResistancePhysicalUnit | null;
  fouling_factor_outside?: OptionalSpecificThermalResistancePhysicalUnit | null;
}

export interface OptionalMediumAPI {
  name?: string | null;
  brine_concentration?: OptionalRatioPhysicalUnit | null;
  custom_name?: string | null;
  reference_temperature?: OptionalTemperaturePhysicalUnit | null;
  custom_density?: OptionalDensityPhysicalUnit | null;
  custom_dynamic_viscosity?: OptionalDynamicViscosityPhysicalUnit | null;
  custom_specific_heat_capacity?: OptionalSpecificHeatCapacityPhysicalUnit | null;
  custom_thermal_conductivity?: OptionalThermalConductivityPhysicalUnit | null;
}

export interface OptionalOilAPI {
  percentage?: RatioPhysicalUnit | null;
  viscosity?: string | null;
}

export interface InterChangableOperatingConditionsAPI {
  temperature_medium_inlet?: OptionalTemperaturePhysicalUnit | null;
  temperature_medium_outlet?: OptionalTemperaturePhysicalUnit | null;
  capacity?: OptionalThermalPowerPhysicalUnit | null;
  volumetric_flow_rate?: OptionalVolumetricFlowRatePhysicalUnit | null;
  temperature_saturation_evaporation?: OptionalTemperaturePhysicalUnit | null;
  tube_length?: OptionalLengthPhysicalUnit | null;
}

// Type definition for refrigerant object
export interface RefrigerantResultAPI {
  name: string;
  saturation_pressure: OptionalPressurePhysicalUnit | null;
  critical_temperature: OptionalTemperaturePhysicalUnit | null;
  critical_pressure: OptionalPressurePhysicalUnit | null;
}

export interface MediumResultAPI {
  name: string;
  mean_temperature: OptionalTemperaturePhysicalUnit | null;
  thermal_conductivity: OptionalThermalConductivityPhysicalUnit | null;
  density: OptionalDensityPhysicalUnit | null;
  dynamic_viscosity: OptionalDynamicViscosityPhysicalUnit | null;
  kinematic_viscosity: OptionalDynamicViscosityPhysicalUnit | null;
  specific_heat_capacity: OptionalSpecificHeatCapacityPhysicalUnit | null;
  prandtl_number: number | null;
  brine_concentration?: OptionalRatioPhysicalUnit | null;
}

export interface ResultBodyFloodedEvaporatorAPI {
  input_data?: OptionalRequestBodyFloodedEvaporatorAPI | null;
  messages?: Message[] | null;
  overall_message_level?: string | null;
  pareto_optimal?: boolean | null;
  capacity?: ThermalPowerPhysicalUnit | null;
  distance_upper_row?: LengthPhysicalUnit | null;
  volumetric_flow_rate?: VolumetricFlowRatePhysicalUnit | null;
  free_space_above?: RatioPhysicalUnit | null;
  free_space_beneath?: RatioPhysicalUnit | null;
  horizontal_tube_pitch?: LengthPhysicalUnit | null;
  overall_heat_transfer_coefficient?: HeatTransferCoefficientPhysicalUnit | null;
  inner_shell_diameter?: LengthPhysicalUnit | null;
  mediumside_pressure_drop?: PressurePhysicalUnit | null;
  mediumside_pressure_drop_total?: PressurePhysicalUnit | null;
  inside_heat_transfer_coefficient?: HeatTransferCoefficientPhysicalUnit | null;
  inside_temperature_mean?: TemperaturePhysicalUnit | null;
  inside_velocity?: VelocityPhysicalUnit | null;
  lmtd?: TemperaturePhysicalUnit | null;
  outside_heat_transfer_coefficient?: HeatTransferCoefficientPhysicalUnit | null;
  outside_heat_flux?: ThermalPowerPhysicalUnit | null;
  over_design?: RatioPhysicalUnit | null;
  inside_fouling_resistance?: RatioPhysicalUnit | null;
  outside_fouling_resistance?: RatioPhysicalUnit | null;
  inside_htc_resistance?: RatioPhysicalUnit | null;
  outside_htc_resistance?: RatioPhysicalUnit | null;
  wall_resistance?: RatioPhysicalUnit | null;
  reynolds_number?: number | null;
  temperature_medium_inlet?: TemperaturePhysicalUnit | null;
  temperature_medium_outlet?: TemperaturePhysicalUnit | null;
  tube_length?: LengthPhysicalUnit | null;
  tube_rows?: number | null;
  tubes_upper_row?: number | null; 
  vertical_tube_pitch?: LengthPhysicalUnit | null;
  heat_transfer_area_outside?: LengthPhysicalUnit | null;
  medium?: MediumResultAPI | null;
  refrigerant?: RefrigerantResultAPI | null;
  wall_overheating?: TemperatureDifferencePhysicalUnit | null;
  temperature_saturation_evaporation_effective?: TemperaturePhysicalUnit | null;
  temperature_saturation_evaporation?: TemperaturePhysicalUnit | null;
}

export interface ExtendedResultBodySpecialFloodedEvaporatorAPI extends ResultBodyFloodedEvaporatorAPI {
  timestamp?: string;
  pareto_optimal?: boolean | null;
  optimization_type?: string;
}

export interface Message {
  message_level: string;
  message_content: string;
}

// Optimization API Types
export interface OptimizationConstraintsUI {
  min_tube_length: OptionalLengthPhysicalUnit;
  max_tube_length: OptionalLengthPhysicalUnit;
  min_flow_velocity: OptionalVelocityPhysicalUnit;
  max_flow_velocity: OptionalVelocityPhysicalUnit;
  max_pressure_drop: OptionalPressurePhysicalUnit;
  max_approach_temperature: OptionalTemperatureDifferencePhysicalUnit;
  preferred_tubes?: string[]; // User-specified preferred tube names
}

// Standard optimization config for customers (100% compactness focus)
export interface OptimizationConfigStandardAPI {
  max_iterations: number;
  
  // User-defined constraints with PhysicalUnit types
  min_tube_length?: LengthPhysicalUnit;
  max_tube_length?: LengthPhysicalUnit;
  min_flow_velocity?: VelocityPhysicalUnit;
  max_flow_velocity?: VelocityPhysicalUnit;
  max_pressure_drop?: PressurePhysicalUnit;
  max_approach_temperature?: TemperatureDifferencePhysicalUnit;
}

// Advanced optimization config for staff (full configurability)
export interface OptimizationConfigAPI {
  max_iterations: number;
  exploration_weight: number;
  
  // Scoring configuration - weights are automatically normalized to sum to 1.0 among enabled objectives
  pressure_drop_weight: number;
  approach_temperature_weight: number;
  compactness_weight: number;
  
  // Enable/disable objectives - at least one must be enabled
  pressure_drop_enabled: boolean;
  approach_temperature_enabled: boolean;
  compactness_enabled: boolean;
  
  // User-defined constraints with PhysicalUnit types
  min_tube_length?: LengthPhysicalUnit;
  max_tube_length?: LengthPhysicalUnit;
  min_flow_velocity?: VelocityPhysicalUnit;
  max_flow_velocity?: VelocityPhysicalUnit;
  max_pressure_drop?: PressurePhysicalUnit;
  max_approach_temperature?: TemperatureDifferencePhysicalUnit;
  preferred_tubes?: string[];
}

// Standard optimization request for customers
export interface RequestBodyOptimizeFloodedEvaporatorStandardAPI {
  evaporator_configuration: OptionalEvaporatorConfigurationAPI;
  fluids: OptionalFluidsAPI;
  system_of_units: string;
  interchangable_input_conditions: InterChangableOperatingConditionsAPI;
  calculation_mode: string;
  optimization_config: OptimizationConfigStandardAPI;
  correction_factor_outside?: number;
  correction_factor_inside?: number;
  baseline_result?: ExtendedResultBodySpecialFloodedEvaporatorAPI;
}

// Advanced optimization request for staff
export interface RequestBodyOptimizeFloodedEvaporatorAPI {
  evaporator_configuration: OptionalEvaporatorConfigurationAPI;
  fluids: OptionalFluidsAPI;
  system_of_units: string;
  interchangable_input_conditions: InterChangableOperatingConditionsAPI;
  calculation_mode: string;
  optimization_config: OptimizationConfigAPI;
  correction_factor_outside?: number;
  correction_factor_inside?: number;
  baseline_result?: ExtendedResultBodySpecialFloodedEvaporatorAPI;
}

export interface OptimizationCandidateAPI {
  tube_count: number;
  tube_length: LengthPhysicalUnit;
  tube_name: string;
  pressure_drop: PressurePhysicalUnit;
  heat_transfer_coefficient: HeatTransferCoefficientPhysicalUnit;
  objective_score: number;
  is_pareto_optimal: boolean;
  optimization_type?: string;  // 'balanced' | 'pressure_drop' | 'heat_transfer' | 'compact'
  calculation_result: ResultBodyFloodedEvaporatorAPI;
}

export interface ResultBodyOptimizeFloodedEvaporatorAPI {
  candidates: OptimizationCandidateAPI[];
  best_candidate: OptimizationCandidateAPI | null;
  optimization_metadata: Record<string, any>;
  messages?: Message[] | null;
  overall_message_level?: string | null;
}

/**
 * Comprehensive service for the Special Flooded Evaporator calculator
 * Combines both UI and calculation functionality
 */
const specialEvaporatorService = {
  // ======================================================
  // UI Related API Endpoints
  // ======================================================
  
  /**
   * Retrieves selection labels for the UI
   */
  getInputLabels: (): Promise<AxiosResponse<SelectionLabelsBoiling>> => 
    api.get<SelectionLabelsBoiling>('/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/ui/input-labels'),

  /**
   * Checks if a tube exists in the database
   * @param tubeName Name of the tube to check
   */
  checkTubeInDB: (tubeName: string, unitSystem: string): Promise<AxiosResponse<TubeUI>> => 
    api.get<TubeUI>(`/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/ui/tubes/is-tube-in-db?tube_name=${tubeName}&unit_system=${unitSystem}`),

  /**
   * Searches for tubes by name or criteria
   * @param searchCriteria Search string
   * @param unitSystem Unit system to use for returned values
   */
  getTubesByName: (searchCriteria: string, unitSystem: string): Promise<AxiosResponse<TubeUI[]>> => 
    api.get<TubeUI[]>(`/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/ui/tubes?search_criteria=${searchCriteria}&unit_system=${unitSystem}`),

  /**
   * Gets unique tube values for dropdown filters
   * @param unitSystem Unit system to use
   */
  getUniqueTubeValues: (unitSystem: string): Promise<AxiosResponse<TubeDropdownValuesAPI>> => 
    api.get<TubeDropdownValuesAPI>(`/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/ui/tubes/unique-values?unit_system=${unitSystem}`),

  /**
   * Advanced query for tubes with multiple filter criteria
   * @param params Query parameters
   */
  queryTubes: (params: {
    unit_system: string,
    tube_family?: string | null,
    material?: string | null,
    diameter_category?: string | null,
    wall_thickness_key?: string | null,
    suitable_for_fouling?: boolean | null,
    refrigerant?: string | null,
    search_criteria?: string | null
  }): Promise<AxiosResponse<TubeUI[]>> => {
    // Build query string from params
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
    
    return api.get<TubeUI[]>(`/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/ui/tubes/query?${queryParams.toString()}`);
  },

  /**
   * Gets filtered dropdown values for tubes based on current filter selections
   * This enables cascading filters for better user experience
   * @param currentFilters Current filter selections
   * @param unitSystem Unit system to use
   */
  getFilteredTubeDropdownValues: (currentFilters: {
    tube_family?: string | null,
    material?: string | null,
    diameter_category?: string | null,
    wall_thickness_key?: string | null,
    refrigerant?: string | null,
    brand_name?: string | null,
    suitable_for_fouling?: boolean | null
  }, unitSystem: string): Promise<AxiosResponse<TubeDropdownValuesAPI>> => {
    // Send filters directly as they match the TubeQuery schema (individual values, not arrays)
    return api.post<TubeDropdownValuesAPI>(
      `/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/ui/tubes/filtered-dropdown-values?unit_system=${unitSystem}`,
      currentFilters
    );
  },

  /**
   * Converts values between unit systems
   * @param formValues Form values to convert
   */
  convertValues: (formValues: OptionalRequestBodyFloodedEvaporatorAPI): Promise<AxiosResponse<OptionalRequestBodyFloodedEvaporatorAPI>> => 
    api.post<OptionalRequestBodyFloodedEvaporatorAPI>('/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/ui/convert-values', formValues),

  /**
   * Maps a payload to the input model
   * @param payload Payload to map
   */
  mapPayloadToInputModel: (payload: any): Promise<AxiosResponse<OptionalRequestBodyFloodedEvaporatorAPI>> => 
    api.post<OptionalRequestBodyFloodedEvaporatorAPI>('/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/ui/map-payload-to-input-model', payload),

  // ======================================================
  // Calculation API Endpoints
  // ======================================================
  
  /**
   * Performs the evaporator calculation
   * @param data Calculation input data
   */
  calculateEvaporator: (data: OptionalRequestBodyFloodedEvaporatorAPI): Promise<AxiosResponse<ResultBodyFloodedEvaporatorAPI>> => 
    api.post<ResultBodyFloodedEvaporatorAPI>('/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/calculate', data),

  /**
   * Performs evaporator optimization
   * @param data Optimization input data
   */
  optimizeEvaporator: (data: RequestBodyOptimizeFloodedEvaporatorAPI): Promise<AxiosResponse<ResultBodyOptimizeFloodedEvaporatorAPI>> => 
    api.post<ResultBodyOptimizeFloodedEvaporatorAPI>('/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/optimize', data),

  // ======================================================
  // Export API Endpoints
  // ======================================================
  
  /**
   * Generates Excel results file
   * @param results Results to export
   * @param name Optional filename
   */
  generateExcelResults: (
    results: ResultBodyFloodedEvaporatorAPI[], 
    name: string = 'special_flooded_evaporator_results'
  ): Promise<AxiosResponse<Blob>> => 
    api.post<Blob>(
      '/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/ui/download/results-excel-file',
      results,
      {
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      }
    ),

  /**
   * Generates a PDF report
   * @param result Result to generate PDF for
   * @param name Optional filename
   * @param share_url Optional share URL generated by frontend
   * @param project_info Optional project information
   */
  generatePDF: (result: ResultBodyFloodedEvaporatorAPI, name: string = 'special_flooded_evaporator_results', share_url?: string, project_info?: any): Promise<AxiosResponse<Blob>> => {
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    
    const payload = {
      result,
      project_info,
      share_url
    };
    
    return api.post<Blob>(
      `/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/ui/download/pdf?${params.toString()}`,
      payload,
      {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      }
    );
  },

  convertTDUFile: (tduData: any): Promise<AxiosResponse<OptionalRequestBodyFloodedEvaporatorAPI>> => 
    api.post<OptionalRequestBodyFloodedEvaporatorAPI>('/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/ui/convert-tdu', tduData),

  convertHTRIValues: (conversionData: {
    refrigerant: string;
    mass_flow_rate: MassFlowRatePhysicalUnit;
    vapor_fraction: number;
    inlet_pressure: PressurePhysicalUnit;
    system_of_units: string;
  }): Promise<AxiosResponse<{
    temperature_saturation: TemperaturePhysicalUnit;
    volumetric_flow_rate: VolumetricFlowRatePhysicalUnit;
  }>> => 
    api.post('/v1/heat-exchanger/shell-and-tube/flooded-evaporator/htri-convert', conversionData),

  parseHTRIFile: (file: File): Promise<AxiosResponse<{
    tube_count?: number;
    tube_length?: { value?: number; unit: string };
    pass_count?: number;
    fouling_factor?: { value?: number; unit: string };
    fouling_factor_refrigerant?: { value?: number; unit: string };
    inlet_temp?: { value?: number; unit: string };
    outlet_temp?: { value?: number; unit: string };
    medium?: string;
    evaporating_temp?: { value?: number; unit: string };
    refrigerant?: string;
    mass_flow_rate_refrigerant?: { value?: number; unit: string };
    mass_flow_rate_medium?: { value?: number; unit: string };
    vapor_fraction_inlet?: number;
    inlet_pressure_refrigerant?: { value?: number; unit: string };
    tube_diameter?: { value?: number; unit: string };
    heat_exchanger_duty?: { value?: number; unit: string };
  }>> => {
    return file.text().then(content => 
      api.post('/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/ui/parse-htri', { file_content: content })
    );
  },

  /**
   * Standard optimization for customers (100% compactness focus)
   * 
   * Available to all authenticated users with permission.
   * - Fixed scoring: 100% compactness (minimize tube count × tube length)
   * - Automatic tube recommendations based on starting tube
   * - No custom scoring weights or tube selection
   * 
   * @param requestBody Optimization request with standard config
   * @returns Optimization results
   */
  optimizeStandard: (requestBody: RequestBodyOptimizeFloodedEvaporatorStandardAPI): Promise<AxiosResponse<ResultBodyOptimizeFloodedEvaporatorAPI>> =>
    api.post<ResultBodyOptimizeFloodedEvaporatorAPI>(
      '/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/optimize',
      requestBody
    ),

  /**
   * Advanced optimization for staff (full configurability)
   * 
   * ⚠️ STAFF-ONLY: Backend enforced with IsStaffUser() decorator.
   * Attempting to call this endpoint as non-staff will result in 403 Forbidden.
   * 
   * Features (proprietary algorithm):
   * - Custom scoring weights for multiple objectives
   * - Manual tube selection or automatic recommendations
   * - Full control over optimization objectives and weights
   * - Weights are normalized by backend to sum to 100%
   * 
   * Security:
   * - Backend enforcement: IsStaffUser() permission decorator
   * - Frontend: Conditional rendering based on user.is_staff
   * - Defense in depth: Multiple layers of access control
   * 
   * @param requestBody Optimization request with advanced config
   * @returns Optimization results
   */
  optimizeAdvanced: (requestBody: RequestBodyOptimizeFloodedEvaporatorAPI): Promise<AxiosResponse<ResultBodyOptimizeFloodedEvaporatorAPI>> =>
    api.post<ResultBodyOptimizeFloodedEvaporatorAPI>(
      '/v1/heat-exchanger/shell-and-tube/flooded-evaporator/tube/optimize/advanced',
      requestBody
    ),
};

export default specialEvaporatorService;
